import React from 'react';
import Icon from '../AppIcon';

const StatusIndicator = ({ 
  status, 
  confidence, 
  variant = 'default',
  size = 'default',
  showIcon = true,
  showText = true,
  className = ''
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'reviewed':
        return {
          color: 'text-success',
          bgColor: 'bg-success/10',
          borderColor: 'border-success/20',
          icon: 'CheckCircle',
          text: 'Reviewed'
        };
      case 'needs_review':
      case 'needs-review':
        return {
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          borderColor: 'border-warning/20',
          icon: 'AlertCircle',
          text: 'Needs Review'
        };
      case 'processing':
        return {
          color: 'text-primary',
          bgColor: 'bg-primary/10',
          borderColor: 'border-primary/20',
          icon: 'Loader2',
          text: 'Processing'
        };
      case 'error':
        return {
          color: 'text-error',
          bgColor: 'bg-error/10',
          borderColor: 'border-error/20',
          icon: 'XCircle',
          text: 'Error'
        };
      case 'pending':
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          borderColor: 'border-border',
          icon: 'Clock',
          text: 'Pending'
        };
      default:
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          borderColor: 'border-border',
          icon: 'Circle',
          text: 'Unknown'
        };
    }
  };

  const getSizeConfig = () => {
    switch (size) {
      case 'sm':
        return {
          container: 'px-2 py-1 text-xs',
          icon: 12,
          gap: 'space-x-1'
        };
      case 'lg':
        return {
          container: 'px-4 py-2 text-base',
          icon: 20,
          gap: 'space-x-3'
        };
      default:
        return {
          container: 'px-3 py-1.5 text-sm',
          icon: 16,
          gap: 'space-x-2'
        };
    }
  };

  const statusConfig = getStatusConfig();
  const sizeConfig = getSizeConfig();

  const renderBadge = () => (
    <div className={`
      inline-flex items-center ${sizeConfig?.gap} rounded-full border font-medium transition-smooth
      ${statusConfig?.color} ${statusConfig?.bgColor} ${statusConfig?.borderColor}
      ${sizeConfig?.container}
      ${className}
    `}>
      {showIcon && (
        <Icon 
          name={statusConfig?.icon} 
          size={sizeConfig?.icon}
          className={status === 'processing' ? 'animate-spin' : ''}
        />
      )}
      {showText && <span>{statusConfig?.text}</span>}
    </div>
  );

  const renderDot = () => (
    <div className={`
      inline-flex items-center ${sizeConfig?.gap}
      ${className}
    `}>
      <div className={`
        w-2 h-2 rounded-full
        ${statusConfig?.bgColor?.replace('/10', '')}
      `} />
      {showText && (
        <span className={`${sizeConfig?.container?.split(' ')?.[2]} ${statusConfig?.color}`}>
          {statusConfig?.text}
        </span>
      )}
    </div>
  );

  const renderIcon = () => (
    <div className={`inline-flex items-center ${sizeConfig?.gap} ${className}`}>
      <Icon 
        name={statusConfig?.icon} 
        size={sizeConfig?.icon}
        className={`${statusConfig?.color} ${status === 'processing' ? 'animate-spin' : ''}`}
      />
      {showText && (
        <span className={`${sizeConfig?.container?.split(' ')?.[2]} ${statusConfig?.color}`}>
          {statusConfig?.text}
        </span>
      )}
    </div>
  );

  // Confidence indicator for AI processing
  const renderConfidence = () => {
    if (typeof confidence !== 'number') return null;
    
    const confidenceColor = confidence >= 0.8 ? 'text-success' : confidence >= 0.6 ? 'text-warning' : 'text-error';
    const confidenceOpacity = `opacity-${Math.round(confidence * 100)}`;
    
    return (
      <div className={`inline-flex items-center space-x-2 ${className}`}>
        <div className={`
          px-2 py-1 rounded text-xs font-mono
          ${confidenceColor} bg-muted border border-border
        `}>
          {Math.round(confidence * 100)}%
        </div>
        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-300 ${confidenceColor?.replace('text-', 'bg-')}`}
            style={{ width: `${confidence * 100}%` }}
          />
        </div>
      </div>
    );
  };

  switch (variant) {
    case 'dot':
      return renderDot();
    case 'icon':
      return renderIcon();
    case 'confidence':
      return renderConfidence();
    default:
      return renderBadge();
  }
};

export default StatusIndicator;
